import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api/client'
import { asList } from '@/lib/api/list'

export type NoteKind = 'note' | 'analysis'
export type NoteVisibility = 'private' | 'team' | 'all'
export type NotePotential = '' | 'low' | 'medium' | 'high'

/** Müşteri notu veya satış öncesi ön analiz. canEdit yalnız yazar ve not yöneticisinde true. */
export type AccountNote = {
  id: string
  kind: NoteKind
  visibility: NoteVisibility
  title: string
  body: string
  needs: string
  solution: string
  potential: NotePotential
  pinned: boolean
  authorId: string
  authorName: string
  canEdit: boolean
  createdAt: string
  updatedAt: string
}

export type NoteInput = Pick<AccountNote, 'kind' | 'visibility' | 'title' | 'body' | 'needs' | 'solution' | 'potential' | 'pinned'>

const key = (customerId: string) => ['crm', 'notes', customerId] as const

function mapNote(r: Record<string, unknown>): AccountNote {
  const s = (v: unknown) => (v == null ? '' : String(v))
  const vis = s(r.visibility)
  const pot = s(r.potential)
  return {
    id: s(r.id),
    kind: r.kind === 'analysis' ? 'analysis' : 'note',
    visibility: vis === 'private' || vis === 'team' ? vis : 'all',
    title: s(r.title),
    body: s(r.body),
    needs: s(r.needs),
    solution: s(r.solution),
    potential: pot === 'low' || pot === 'medium' || pot === 'high' ? pot : '',
    pinned: r.pinned === true,
    authorId: s(r.author_id),
    authorName: s(r.author_name),
    canEdit: r.can_edit === true,
    createdAt: s(r.created_at),
    updatedAt: s(r.updated_at),
  }
}

export const useAccountNotes = (customerId: string) =>
  useQuery({
    queryKey: key(customerId),
    enabled: !!customerId,
    queryFn: async () => asList(await api.get<Record<string, unknown>[]>(`/api/v1/accounts/${customerId}/notes`)).map(mapNote),
  })

export function useNoteMutations(customerId: string) {
  const qc = useQueryClient()
  const onSuccess = () => qc.invalidateQueries({ queryKey: key(customerId) })
  const save = useMutation({
    mutationFn: ({ id, input }: { id?: string; input: NoteInput }) =>
      id ? api.put(`/api/v1/accounts/${customerId}/notes/${id}`, input) : api.post(`/api/v1/accounts/${customerId}/notes`, input),
    onSuccess,
  })
  const remove = useMutation({ mutationFn: (id: string) => api.del(`/api/v1/accounts/${customerId}/notes/${id}`), onSuccess })
  return { save, remove }
}
